import { BaseService } from '../base/BaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

export interface SchedulingPreferences {
  earliestHour: number;
  latestHour: number;
  activeDays: number[];
}

export interface UserPreferences {
  id?: string;
  user_id: string;
  acknowledged_competitor_privacy?: boolean;
  scheduling_preferences?: SchedulingPreferences;
  timezone?: string | null;
  created_at?: string;
  updated_at?: string;
}

export type UserPreferencesInsert = Omit<UserPreferences, 'id' | 'created_at' | 'updated_at'> & {
  created_at?: string;
  updated_at?: string;
};

export type UserPreferencesUpdate = Partial<
  Omit<UserPreferences, 'id' | 'user_id' | 'created_at'>
> & {
  updated_at?: string;
};

const USER_CONFLICT = 'user_id';

export class UserPreferencesService extends BaseService {
  private static instance: UserPreferencesService;

  public static getInstance(): UserPreferencesService {
    if (!UserPreferencesService.instance) {
      UserPreferencesService.instance = new UserPreferencesService();
    }
    return UserPreferencesService.instance;
  }

  async getUserPreferences(): Promise<ServiceResponse<UserPreferences>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const cacheKey = `user-preferences-${userId}`;

      const cached = this.getFromCache<UserPreferences>(cacheKey);
      if (cached) {
        return cached;
      }

      const { data, error } = await this.client
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        throw error;
      }
      if (!data) {
        return { user_id: userId } as UserPreferences;
      }

      this.setCache(cacheKey, data, 10 * 60 * 1000);

      return data;
    }, 'Failed to get user preferences');
  }

  async upsertPreferences(
    updates: Partial<UserPreferencesInsert>,
  ): Promise<ServiceResponse<UserPreferences>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const preferencesData: UserPreferencesInsert = {
        user_id: userId,
        updated_at: new Date().toISOString(),
        ...updates,
      };

      const { data, error } = await this.client
        .from('user_preferences')
        .upsert({ ...preferencesData, user_id: userId } as UserPreferencesInsert, {
          onConflict: USER_CONFLICT,
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Failed to update user preferences');

      this.clearCache(`user-preferences-${userId}`);

      return data;
    }, 'Failed to update user preferences');
  }

  async updatePreference<K extends keyof UserPreferencesUpdate>(
    field: K,
    value: UserPreferencesUpdate[K],
  ): Promise<ServiceResponse<UserPreferences>> {
    return this.execute(
      async () => {
        const userId = await this.ensureAuthenticated();

        const existing = await this.getUserPreferences();
        if (existing.error) {
          throw new Error(
            typeof existing.error === 'string' ? existing.error : existing.error.message,
          );
        }

        if (!existing.data) {
          const insertData: Partial<UserPreferencesInsert> = {
            user_id: userId,
            [field]: value,
          };
          const result = await this.upsertPreferences(insertData as UserPreferencesInsert);
          if (result.error)
            throw new Error(typeof result.error === 'string' ? result.error : result.error.message);
          return result.data!;
        }

        const updateData: Partial<UserPreferencesInsert> = {
          user_id: userId,
          [field]: value,
        };

        const { data, error } = await this.client
          .from('user_preferences')
          .upsert(updateData as UserPreferencesInsert, { onConflict: USER_CONFLICT })
          .select()
          .maybeSingle();

        if (error) throw error;
        if (!data) throw new Error('Failed to update preference');

        this.clearCache(`user-preferences-${userId}`);

        return data;
      },
      `Failed to update preference: ${String(field)}`,
    );
  }

  async updateUserPreferences(
    updates: Partial<Omit<UserPreferencesInsert, 'user_id'>>,
  ): Promise<ServiceResponse<UserPreferences>> {
    return this.upsertPreferences(updates);
  }

  async acknowledgeCompetitorPrivacy(): Promise<ServiceResponse<UserPreferences>> {
    return this.updatePreference('acknowledged_competitor_privacy', true);
  }

  async hasAcknowledgedCompetitorPrivacy(): Promise<ServiceResponse<boolean>> {
    return this.execute(async () => {
      const preferences = await this.getUserPreferences();
      if (preferences.error) {
        throw new Error(
          typeof preferences.error === 'string' ? preferences.error : preferences.error.message,
        );
      }

      return preferences.data?.acknowledged_competitor_privacy === true;
    }, 'Failed to check competitor privacy acknowledgment');
  }

  clearUserPreferencesCache(): void {
    const userId = this.getCacheUserId();
    if (userId) {
      this.clearCache(`user-preferences-${userId}`);
    }
  }

  private getCacheUserId(): string | null {
    return null;
  }
}

export const userPreferencesService = UserPreferencesService.getInstance();
